import { createTheme } from "@dash/default-theme";
import { Nav, SiteContainer } from "@dash/nav";
import { Theme, ThemeProvider } from "@emotion/react";
import {
  ACCOUNTS_URL,
  API_URL,
  CRM_URL,
  DOCS_URL,
  INVOICES_URL,
  POINTS_URL,
  QR_SYNC_URL,
  useConfig,
} from "@megarax/dev-config";
import { DocsPage } from "@megarax/dev-docs";
import { EnvironmentPage } from "@megarax/dev-environment";
import { NotificationDetailsPage, NotificationsPage } from "@megarax/dev-notifications";
import { appLinkTreeToNavEntries, MegaraxNavFooter } from "@megarax/features";
import { filterLinkTree } from "@megarax/home";
import { useContext, useEffect, useState } from "react";
import { MdOutlineComputer } from "react-icons/md";
import { Outlet, Route } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { LinkDoc } from "@megaron/docs-contracts";

const devTheme: Theme = createTheme("205");

type Props = {
  linkDocs: LinkDoc[] | undefined;
};

export const useDevRouter = ({ linkDocs }: Props) => {
  return (
    <Route
      path="/dev"
      element={
        <ThemeProvider theme={devTheme}>
          <DevWebappContainer linkDocs={linkDocs} />
        </ThemeProvider>
      }
    >
      <Route path="environment" element={<EnvironmentPage />} />
      <Route path="docs/*" element={<DocsPage />} />
      <Route path="notifications/*" element={<NotificationsPage />} />
      <Route path="notifications/uuid/:uuid" element={<NotificationDetailsPage />} />
    </Route>
  );
};

const environmentTemplate = process.env.NX_PUBLIC_ENVIRONMENT_TEMPLATE;

const DevWebappContainer: React.FC<{
  linkDocs: LinkDoc[] | undefined;
}> = ({ linkDocs }) => {
  const fullLinkTree = filterLinkTree(linkDocs || []);
  const devLinkTree = fullLinkTree.find((appTree) => appTree.app === "dev");

  const { iamUser, isLoaded } = useContext(IamAuthContext);

  const { config } = useConfig();
  const { accountsUrl, apiUrl, crmUrl, docsUrl, invoicesUrl, pointsUrl, qrSyncUrl } = config;

  const [isProductionUsed, setIsProductionUsed] = useState<boolean>(false);

  useEffect(() => {
    if (
      apiUrl === API_URL ||
      crmUrl === CRM_URL ||
      docsUrl === DOCS_URL ||
      invoicesUrl === INVOICES_URL ||
      pointsUrl === POINTS_URL ||
      accountsUrl === ACCOUNTS_URL ||
      qrSyncUrl === QR_SYNC_URL
    ) {
      setIsProductionUsed(true);
      return;
    }

    setIsProductionUsed(false);
  }, [accountsUrl, apiUrl, crmUrl, docsUrl, invoicesUrl, pointsUrl, qrSyncUrl]);

  if (!isLoaded) return null;

  return (
    <SiteContainer
      nav={
        <Nav
          app="dev"
          title="DEV"
          subtitle="Megarax.net"
          logo={<MdOutlineComputer width="1em" height="1em" />}
          footer={iamUser && <MegaraxNavFooter />}
          entries={appLinkTreeToNavEntries(devLinkTree)}
          showProductionEnvAlert={isProductionUsed && !environmentTemplate}
        />
      }
    >
      <Outlet />
    </SiteContainer>
  );
};
